import React, { useEffect, useState } from 'react'
import Gap from './Gap'
import FormInput from './FormInput'
import SubmitButton from './SubmitButton'
import SheetHeader from './SheetHeader'
import { bookmarkApi, ImportedPlaceSummary } from '../api/bookmark'
import ImportGroupPicker from './ImportGroupPicker'
import { useImportGroupSelection } from '../hooks/useImportGroupSelection'
import { toastUtil } from '../utils/toast'
import { registerOpenSheet } from '../utils/sheetOpenState'
import { firstUrlIn } from '../utils/sharedLink'
import ImportPlaceSearchResults from './ImportPlaceSearchResults'

export interface ImportListContentProps {
  /** 뒤로가기(부모 시트의 기본 뷰로 전환). 가져오는 중엔 무시. */
  onBack: () => void
  /**
   * 링크 해석 성공 시 호출. 폴더명(이름칸 채우기)과 가져온 place_id 목록을 전달.
   * 실제 리스트 생성은 부모(추가 시트)의 "완료"에서 이뤄진다.
   */
  onResolved: (folderName: string | null, placeIds: number[]) => void
  /**
   * 가게 이름 검색에서 한 곳을 고를 때 호출. 링크와 달리 지금까지 담은 목록에
   * '더한다'(같은 장소는 한 번만). 새로 담겼으면 true, 이미 있었으면 false.
   */
  onPlacePicked: (placeId: number) => boolean
}

/**
 * "장소 가져오기" 내용 컴포넌트 (바텀시트 chrome 없음).
 * 부모 시트(BookmarkCategoryAddModal) 안에서 step 전환으로 렌더된다 — 별도 레이어로
 * 쌓지 않고 멤버 초대와 동일한 전환 패턴을 쓰기 위함.
 * 네이버지도/카카오맵 공유 리스트 링크를 서버에서 해석(장소 등록/매칭)만 하고,
 * 폴더명 + place_id 목록을 부모에게 돌려준다.
 */
const ImportListContent: React.FC<ImportListContentProps> = ({
  onBack,
  onResolved,
  onPlacePicked,
}) => {
  /*
   * 이 화면에서는 스와이프 뒤로가기를 끈다.
   *
   * 묶음 목록을 세로로 스크롤하는데, 손가락이 조금만 옆으로 흘러도 전역
   * 스와이프 제스처가 잡아채 스크롤이 안 되고 화면이 뒤로 넘어갔다.
   * 시트가 열려 있을 때 쓰는 기존 장치(registerOpenSheet)를 그대로 쓴다 —
   * useSwipeBack 이 isAnySheetOpen() 을 보고 비켜 준다.
   */
  useEffect(() => registerOpenSheet(), [])

  const [url, setUrl] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  /*
   * 묶음이 나뉜 링크(네이버 기획전)를 넣으면 서버가 담지 않고 묶음 목록만
   * 돌려준다. 그때 입력칸 대신 고르는 화면을 보여준다.
   */
  const pick = useImportGroupSelection()
  const [groupPlaces, setGroupPlaces] = useState<ImportedPlaceSummary[]>([])

  /*
   * 입력칸에 주소가 없으면 가게 이름 검색으로 본다.
   * 서버에 링크로 보내지 않고, 아래에 검색 결과를 띄워 한 곳을 고르게 한다.
   */
  const searchKeyword =
    !pick.groups && !isSubmitting && url.trim() && !firstUrlIn(url) ? url : ''

  /*
   * 이 화면에서 검색으로 담은 수.
   * 고른 뒤에도 화면에 남아 이어서 검색할 수 있게 하고(온보딩과 같다),
   * 하나라도 담았으면 입력칸이 비어 있어도 '완료'로 돌아갈 수 있게 한다.
   */
  const [pickedCount, setPickedCount] = useState(0)

  /** 검색에서 고른 가게 — 기존 목록을 바꾸지 않고 더한다 */
  const handlePickSearched = (place: ImportedPlaceSummary) => {
    setUrl('')
    if (onPlacePicked(place.id)) {
      setPickedCount((n) => n + 1)
      toastUtil.success(`'${place.name}' 담았어요`)
    } else {
      toastUtil.success(`'${place.name}'은(는) 이미 담았어요`)
    }
  }

  const handleSubmit = async () => {
    const trimmed = url.trim()
    if (isSubmitting) return
    // 검색으로 담은 뒤 입력칸이 비어 있으면 '완료' = 돌아가기
    if (!trimmed && !pick.groups && pickedCount > 0) {
      onBack()
      return
    }
    if (!trimmed) return
    // 링크가 아니면 서버에 보내지 않는다 (아래 검색 결과에서 고른다)
    if (!pick.groups && !firstUrlIn(trimmed)) return

    setIsSubmitting(true)
    try {
      /*
       * 지역을 고르는 중이면 서버를 다시 부르지 않는다.
       * 첫 조회에서 받은 묶음별 place_ids 를 그대로 거른다.
       */
      if (pick.groups) {
        const ids = pick.selected
        // 공개 리스트에서 한 리스트 안의 곳만 골랐으면 그 리스트 이름
        const name = pick.folderName

        setUrl('')
        pick.reset()
        toastUtil.success(`${ids.length}곳을 가져왔어요`)
        onResolved(name, ids)
        return
      }

      const res = await bookmarkApi.resolveSharedLink(trimmed)

      // 지역을 고를 수 있는 링크다. 서버가 이미 다 찾아 뒀고 묶음만 받았다.
      if (res.needsGroupChoice && res.groups?.length) {
        pick.start(res.groups, res.folder_name, [], {
          groupKind: res.groupKind,
          ownerName: res.ownerName,
        })
        setGroupPlaces(res.places ?? [])
        return
      }

      setUrl('')
      pick.reset()

      toastUtil.success(`${res.imported}곳을 가져왔어요`)
      onResolved(res.folder_name, res.place_ids)
    } catch {
      // 에러 토스트는 apiClient 인터셉터가 백엔드 메시지로 처리 (중복 방지)
    } finally {
      setIsSubmitting(false)
    }
  }

  // 지역을 고르는 중에는 뒤로가기가 입력칸으로 돌아간다
  const handleBack = () => {
    if (isSubmitting) return
    // 묶음 안을 보는 중이면 묶음 목록으로
    if (pick.openedGroupId) {
      pick.setOpenedGroupId(null)
      return
    }
    if (pick.groups) {
      pick.reset()
      return
    }
    onBack()
  }

  return (
    <div className="w-full h-full flex flex-col min-h-0">
      {/* 헤더 (공용) — 뒤로가기로 기본 뷰 전환 */}
      <SheetHeader
        title="장소 가져오기"
        leftAction={
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center justify-center cursor-pointer"
          >
            <img
              src="/images/back-arrow-icon.png"
              alt="뒤로가기"
              draggable={false}
              className="w-48px h-48px object-contain"
            />
          </button>
        }
      />

      {/* 본문 (스크롤) */}
      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide flex flex-col">
        <Gap height={20} />

        {/*
          라벨을 FormInput 에 맡긴다. 밖에서 직접 그리면 크기와 간격을 손으로
          적게 되는데, 그러다 앱 표준(16px / mb-10px)과 어긋나 있었다
          — 입력칸 라벨은 ProfileEdit·Register·PlaceSuggestBottomSheet 등
          앱 전체가 16px 을 쓴다.
        */}
        {pick.groups ? (
          <div className="px-20px">
            <ImportGroupPicker
              title={pick.title}
              groups={pick.groups}
              places={groupPlaces}
              selectedPlaceIds={pick.selected}
              onTogglePlace={pick.togglePlace}
              onToggleGroup={pick.toggleGroup}
              onToggleAll={pick.toggleAll}
              openedGroupId={pick.openedGroupId}
              onOpenGroup={pick.setOpenedGroupId}
              groupKind={pick.groupKind}
              ownerName={pick.ownerName}
            />
          </div>
        ) : (
          <>
            <FormInput
              label="지도 링크 · 가게 이름"
              name="importUrl"
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="리스트는 지도 앱에서 '공개'로 바꾼 후 복사하기"
              marginBottom={0}
              addClassName="px-20px"
            />
            {/* 입력칸 아래 남은 자리를 채운다 — 빈 결과 문구가 이 칸 가운데에 온다 */}
            {searchKeyword && (
              <div className="relative flex-1 px-20px mt-20px">
                <ImportPlaceSearchResults
                  keyword={searchKeyword}
                  onPick={handlePickSearched}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* 완료 버튼 (하단 고정 — 앱 표준 pb-32px) */}
      <div className="px-20px pb-32px">
        <SubmitButton
          text="완료"
          onSubmit={handleSubmit}
          onDisabled={handleSubmit}
          disabled={
            isSubmitting ||
            (pick.groups
              ? pick.selected.length === 0
              : (!url.trim() && pickedCount === 0) || !!searchKeyword)
          }
          loading={isSubmitting}
        />
      </div>
    </div>
  )
}

export default ImportListContent
